import { Draft, produceWithPatches } from 'immer';
import { EntitySize, GridEntity, IRange, Sheet, SheetView } from '../../types';
import {
  FreezeGrid,
  GroupUngroupGrid,
  HideUnhideGrid,
  ResizeGrid,
  SheetCommandType,
} from '../../types/commands/sheetCommands/sheetCommands';
import {
  addRangeToExistingRanges,
  removeRangeFromExistingRanges,
} from '../area/range';
import { SheetManagementSet } from '../../types/sheet/draft';
import { saveSheetUpdatesInSet } from '../sheet/updates';
import {
  addRangeToEntitySizes,
  addRangesToGroups,
  adjustSelectionOnHideUnhide,
  generateResizeRanges,
  getRangesFromSelection,
  mergeConsecutiveEntitySizes,
  removeGroupById,
} from './gridCommandHelpers';
import { t } from 'i18next';
import { SheetDefaults } from '../../constants/sheetDefaults';
import _ from 'lodash';
import {
  freezeEntityCommand,
  hideUnhideEntityCommand,
  resizeEntityCommand,
} from '../commands/generate/generateSheetCommands';

// #region hide un hide
export const hideUnhideGridRanges = (
  set: SheetManagementSet,
  sheet: Draft<Sheet>,
  sheetView: SheetView,
  command: HideUnhideGrid
) => {
  const { sheetId, entity, ranges, type } = command;

  const isColumn = entity === GridEntity.Column;

  // const sheetRecipe: Pick<Sheet, 'hiddenRows' | 'hiddenCols' | 'id'> = {
  //   id: sheet.id,
  //   hiddenRows: sheet.hiddenRows,
  //   hiddenCols: sheet.hiddenCols,
  // };

  const targetRanges = ranges ?? getRangesFromSelection(sheetView, entity);

  let revealedRanges: IRange[] | undefined;

  let updatedRanges = isColumn ? sheet.hiddenCols : sheet.hiddenRows;
  // updatedRanges = _.cloneDeep(updatedRanges)

  targetRanges.forEach((range) => {
    if (type === SheetCommandType.HideGrid) {
      updatedRanges = addRangeToExistingRanges(updatedRanges, range);
    } else {
      const result = removeRangeFromExistingRanges(updatedRanges, range);
      updatedRanges = result.updatedRanges;
      revealedRanges = result.revealedRanges;
    }
  });

  // const [updatedSheet, changes] = produceWithPatches(sheetRecipe, (draft) => {
  const hAlias = isColumn ? 'hiddenCols' : 'hiddenRows';
  sheet[hAlias] = updatedRanges;
  // });

  // saveSheetUpdatesInSet(sheetId, set, { updatedSheet, changes });

  hideUnhideEntityCommand(set, sheetId, command, revealedRanges);

  adjustSelectionOnHideUnhide(
    set,
    sheet,
    sheetView,
    // updatedSheet,
    ranges,
    command,
    revealedRanges
  );

  // TODO: On Hide If Row hidden and filter applied remove hidden rows from filter
  // TODO: On UnHide If new visible rows passes filter add rows to filter
};
// #endregion

// #region freeze un freeze
export const freezeGridRanges = (
  set: SheetManagementSet,
  sheet: Draft<Sheet>,
  command: FreezeGrid
) => {
  const { range, entity, sheetId } = command;

  const isColumn = entity === GridEntity.Column;
  const selAlias = isColumn ? 'ci' : 'ri';
  const freezeAlias = isColumn ? 'freezedCols' : 'freezedRows';

  // const sheetRecipe: Pick<Sheet, 'id' | 'freezedCols' | 'freezedRows'> = {
  //   id: sheetId,
  //   freezedCols: sheet.freezedCols,
  //   freezedRows: sheet.freezedRows,
  // };

  const oldFreeze = sheet[freezeAlias];

  // const [updatedSheet, changes] = produceWithPatches(sheetRecipe, (draft) => {
  let start = -1,
    end = -1;

  let isFrozen = range.start >= 0 && range.end >= 0;
  if (isFrozen) {
    //  type === SheetCommandType.FreezeGrid
    sheet.merges.forEach((area) => {
      start = range.start;
      end = range.end;

      const mergeRange = {
        start: area.start[selAlias],
        end: area.end[selAlias],
      };
      if (end >= mergeRange.start && end < mergeRange.end) {
        const message = t(`errors.sheet.freezeError.message`, {
          entityName: entity,
        });
        throw new Error(message);
      }
    });
  }
  sheet[freezeAlias] = {
    frozen: isFrozen,
    start,
    end,
  };
  // });

  // saveSheetUpdatesInSet(sheetId, set, { updatedSheet, changes });

  freezeEntityCommand(set, sheetId, command, oldFreeze);
};
// #endregion

// #region group un group
export const groupUngroupGridRanges = (
  set: SheetManagementSet,
  sheet: Draft<Sheet>,
  command: GroupUngroupGrid
) => {
  const sheetId = sheet.id;
  const { entity, ranges, groupId, type } = command;

  const isColumn = entity === GridEntity.Column;
  if (type === SheetCommandType.GroupGrid && !ranges) return {};
  if (type === SheetCommandType.UngroupGrid && !groupId) return {};

  // const sheetRecipe: Pick<Sheet, 'id' | 'colGroups' | 'rowGroups'> = {
  //   id: sheetId,
  //   colGroups: sheet.colGroups,
  //   rowGroups: sheet.rowGroups,
  // };

  let targetGroups = isColumn ? sheet.colGroups : sheet.rowGroups;
  if (type === SheetCommandType.GroupGrid && ranges) {
    targetGroups = addRangesToGroups(targetGroups, ranges);
  } else if (type === SheetCommandType.UngroupGrid && groupId) {
    targetGroups = removeGroupById(targetGroups, groupId);
  }

  // const [updatedSheet, changes] = produceWithPatches(sheetRecipe, (draft) => {
  const groupKey = isColumn ? 'colGroups' : 'rowGroups';
  sheet[groupKey] = targetGroups;
  // });

  // saveSheetUpdatesInSet(sheetId, set, { updatedSheet, changes });

  // TODO: Implemenent Group Undo Redo
};
// #endregion

// #region resize grid ranges
export const resizeGridRanges = (
  set: SheetManagementSet,
  sheet: Draft<Sheet>,
  sheetView: SheetView,
  command: ResizeGrid
) => {
  const sheetId = sheet.id;
  const { entity } = command;
  const isColumn = entity === GridEntity.Column;

  // const recipe: Pick<Sheet, 'id' | 'colSizes' | 'rowSizes'> = {
  //   id: sheetId,
  //   colSizes: sheet.colSizes,
  //   rowSizes: sheet.rowSizes,
  // };

  const targetRangeSizes = generateResizeRanges(sheetView, command);

  if (!targetRangeSizes.length) return;

  // Command ON DB should always contains range sizes and no position indexes (on reload this will affect the selected rows)
  delete command.positionIndex;
  command.rangeSizes = targetRangeSizes;

  const sizes = isColumn ? sheet.colSizes : sheet.rowSizes;
  const defaultSize = isColumn ? sheet.defaultColWidth : sheet.defaultRowHeight;

  let updatedSizes = _.cloneDeep(sizes);
  let oldSizes: EntitySize[] = [];
  for (const rangeSize of targetRangeSizes) {
    if (rangeSize.size <= SheetDefaults.minEntitySize) {
      const hideCommand: HideUnhideGrid = {
        type: SheetCommandType.HideGrid,
        sheetId,
        entity,
        ranges: [{ start: rangeSize.start, end: rangeSize.end }],
      };
      hideUnhideGridRanges(set, sheet, sheetView, hideCommand);
    } else {
      const result = addRangeToEntitySizes(
        updatedSizes,
        rangeSize,
        defaultSize
      );

      updatedSizes = result.updatedSizes;

      oldSizes.push(...result.oldSizes);
    }
  }
  if (!oldSizes.length) return; // In case All are hidden cases then oldSizes will be empty
  oldSizes = mergeConsecutiveEntitySizes(oldSizes);
  updatedSizes = updatedSizes.filter(
    (rangeSize) => rangeSize.size !== defaultSize
  );

  // const [updatedSheet, changes] = produceWithPatches(recipe, (draft) => {
  const key = isColumn ? 'colSizes' : 'rowSizes';
  sheet[key] = updatedSizes;
  // });

  // saveSheetUpdatesInSet(sheetId, set, { updatedSheet, changes });

  resizeEntityCommand(set, sheetId, command, oldSizes);
};
// #endregion
